/* eslint-disable no-console */
import { jwtDecode } from "jwt-decode"
import type { Socket } from "socket.io-client"

import { addCoachingChatSocketBreadcrumb } from "@/3514/utils"
import { EStorageServiceType, SessionStorageServiceKeys, StorageService } from "@/services"

import type {
    ICoachingChatSocketClientEvents as IClientEvents,
    ICoachingChatSocketServerEvents as IServerEvents
} from "../../types"

type TSocket = Socket<IServerEvents, IClientEvents>

const LOG_PREFIX = "[cm-ws]"

const storageService: StorageService = new StorageService(EStorageServiceType.Session)

// Reasons that represent a deliberate/expected close (not a fault worth escalating).
const EXPECTED_DISCONNECT_REASONS: ReadonlySet<string> = new Set(["io client disconnect"])

let connectedAtMs: number | null = null
let isAttached = false

function nowMs(): number {
    return Date.now()
}

function tokenTtlSeconds(): number | null {
    const token: string = storageService.getItem(SessionStorageServiceKeys.ChatSocketToken()) ?? ""
    if (!token) return null
    try {
        const exp: number | undefined = jwtDecode<{ exp?: number }>(token).exp
        if (!exp) return null
        return Math.round((exp * 1000 - nowMs()) / 1000)
    } catch {
        return null
    }
}

function sessionDurationSeconds(): number | null {
    if (connectedAtMs === null) return null
    return Math.round((nowMs() - connectedAtMs) / 1000)
}

function transportName(socket: TSocket): string | undefined {
    return socket.io?.engine?.transport?.name
}

function baseContext(socket: TSocket): Record<string, unknown> {
    return {
        ts: new Date(nowMs()).toISOString(),
        socketId: socket.id,
        transport: transportName(socket),
        tokenTtlSec: tokenTtlSeconds(),
        sessionDurationSec: sessionDurationSeconds()
    }
}

function log(
    level: "info" | "warn" | "error",
    event: string,
    socket: TSocket,
    data?: Record<string, unknown>
): void {
    const payload: Record<string, unknown> = { event, ...baseContext(socket), ...data }
    // console.error is auto-forwarded to Sentry (captureConsoleIntegration levels: ["error"]).
    console[level](`${LOG_PREFIX} ${event}`, payload)
    addCoachingChatSocketBreadcrumb(
        `${LOG_PREFIX} ${event}`,
        payload,
        level === "error" ? "error" : level === "warn" ? "warning" : "info"
    )
}

function attachCoachingChatSocketDiagnostics(socket: TSocket): void {
    if (isAttached) return
    isAttached = true

    // --- Socket (namespace) level ---
    socket.on("connect", (): void => {
        connectedAtMs = nowMs()
        log("info", "connect", socket, { recovered: socket.recovered })
    })

    socket.on("disconnect", (reason: Socket.DisconnectReason, description?: unknown): void => {
        const expected: boolean = EXPECTED_DISCONNECT_REASONS.has(reason)
        // Unexpected drops (e.g. "ping timeout", "transport close", "transport error",
        // "io server disconnect") are the ones we're hunting — surface them to Sentry.
        log(expected ? "info" : "error", "disconnect", socket, {
            reason,
            description: description instanceof Error ? description.message : description
        })
        connectedAtMs = null
    })

    socket.on("connect_error", (err: Error): void => {
        log("warn", "connect_error", socket, { message: err?.message, name: err?.name })
    })

    // --- Manager (reconnection engine) level ---
    const manager = socket.io

    manager.on("reconnect_attempt", (attempt: number): void =>
        log("info", "reconnect_attempt", socket, { attempt })
    )
    manager.on("reconnect", (attempt: number): void => log("info", "reconnect", socket, { attempt }))
    manager.on("reconnect_error", (err: Error): void =>
        log("warn", "reconnect_error", socket, { message: err?.message })
    )
    manager.on("reconnect_failed", (): void => log("error", "reconnect_failed", socket))

    // --- Engine (transport) level: the underlying close reason ---
    manager.on("open", (): void => {
        const engine = manager.engine
        if (!engine) return

        engine.on("upgrade", (): void => log("info", "engine.upgrade", socket))
        engine.on("close", (reason: string): void =>
            log("warn", "engine.close", socket, { reason })
        )
        engine.on("error", (err: unknown): void =>
            log("warn", "engine.error", socket, {
                message: err instanceof Error ? err.message : String(err)
            })
        )
    })
}

export { attachCoachingChatSocketDiagnostics }
